import { parseTradeCalendarDate } from "./tradeFinancialUtils";
import {
  formatAgentSortName,
  formatAgentGroupLabel,
  formatReportMoney,
  formatReportDate,
  formatTradeAddress,
} from "./commissionReportUtils";

export { formatAgentSortName, formatAgentGroupLabel, formatReportMoney };

export const PENDING_TRADE_STATUSES = [
  { value: "ALL", label: "All Statuses" },
  { value: "Sold", label: "Sold" },
  { value: "Lease", label: "Lease" },
];

export const PENDING_TRADE_SORT_OPTIONS = [
  { value: "entryDate", label: "Entry Date" },
  { value: "firmDate", label: "Firm Date" },
  { value: "offerDate", label: "Offer Date" },
  { value: "closeDate", label: "Closed Date" },
  { value: "finalizedDate", label: "Finalized Date" },
];

export const PENDING_TRADE_CLASSIFICATIONS = [
  { value: "ALL", label: "All Classifications" },
  { value: "LISTING SIDE", label: "Listing Side" },
  { value: "CO-OPERATING SIDE", label: "Co-Operating Side" },
  { value: "AGENT DOUBLE ENDER", label: "Agent Double Ender" },
];

export const PENDING_TRADE_PROPERTY_TYPES = [
  { value: "ALL", label: "All Property Types" },
  { value: "Residential", label: "Residential" },
  { value: "Commercial", label: "Commercial" },
];

function isReferralRow(row) {
  return (
    String(row?.classification || "")
      .trim()
      .toUpperCase() === "REFERRAL"
  );
}

export function getPendingTradeSortDate(trade, sortBy) {
  const k = trade?.keyInfo || {};
  switch (sortBy) {
    case "firmDate":
      return parseTradeCalendarDate(k.firmDate);
    case "offerDate":
      return parseTradeCalendarDate(k.offerDate);
    case "finalizedDate":
      return parseTradeCalendarDate(k.finalizedDate);
    case "closeDate":
      return (
        parseTradeCalendarDate(k.closeDate) ||
        parseTradeCalendarDate(
          trade?.commission?.saleClosingRows?.[0]?.closingDate,
        )
      );
    case "entryDate":
    default:
      return (
        parseTradeCalendarDate(k.entryDate) ||
        parseTradeCalendarDate(trade?.createdAt)
      );
  }
}

export function pendingTradeDateInRange(trade, sortBy, fromDate, toDate) {
  const d = getPendingTradeSortDate(trade, sortBy);
  if (!d) return false;
  const start = parseTradeCalendarDate(fromDate);
  const end = parseTradeCalendarDate(toDate);
  if (!start || !end) return false;
  start.setHours(0, 0, 0, 0);
  end.setHours(23, 59, 59, 999);
  return d >= start && d <= end;
}

function matchesStatusFilter(trade, status) {
  if (!status || status === "ALL") return true;
  const k = trade?.keyInfo || {};
  const tradeStatus = String(k.status || "").toUpperCase();
  const dealType = String(k.dealType || "").toUpperCase();
  if (status === "Sold") {
    return tradeStatus === "SOLD" || dealType === "SALE";
  }
  if (status === "Lease") {
    return (
      tradeStatus === "LEASED" ||
      tradeStatus === "LEASE" ||
      dealType === "LEASE"
    );
  }
  return true;
}

function matchesPropertyType(trade, propertyType) {
  if (!propertyType || propertyType === "ALL") return true;
  return (
    String(trade?.keyInfo?.propertyType || "").toLowerCase() ===
    String(propertyType).toLowerCase()
  );
}

function matchesClassification(trade, classification) {
  if (!classification || classification === "ALL") return true;
  return (
    String(trade?.keyInfo?.classification || "").toUpperCase() ===
    classification.toUpperCase()
  );
}

function propertyTypeAbbrev(propertyType) {
  const p = String(propertyType || "").toLowerCase();
  if (p.startsWith("res")) return "Res.";
  if (p.startsWith("com")) return "Com.";
  return propertyType || "";
}

function firmCondFlag(trade) {
  const k = trade?.keyInfo || {};
  const status = String(k.status || "").toUpperCase();
  const dealType = String(k.dealType || "").toUpperCase();

  if (
    status === "LEASED" ||
    status === "LEASE" ||
    dealType === "LEASE"
  ) {
    return "Leased";
  }
  if (status === "SOLD" || dealType === "SALE") {
    return "Sold";
  }
  return "";
}

function agentRowMatchesSelected(row, selectedAgent) {
  if (!selectedAgent) return true;
  const emp = String(selectedAgent.employeeNo ?? "").trim();
  const id = String(selectedAgent._id ?? "").trim();
  const rowId = String(row.agentId ?? "").trim();
  const rowName = String(row.agentName || "")
    .trim()
    .toLowerCase();
  const selectedName = formatAgentSortName(selectedAgent).toLowerCase();
  const fullName = `${selectedAgent.firstName || ""} ${selectedAgent.lastName || ""}`
    .trim()
    .toLowerCase();

  if (emp && rowId === emp) return true;
  if (id && rowId === id) return true;
  if (rowName && (rowName === selectedName || rowName === fullName)) return true;
  return false;
}

function resolveAgentMaster(row, agentsByEmp, agentsById) {
  const rowId = String(row.agentId ?? "").trim();
  if (rowId && agentsByEmp.has(rowId)) return agentsByEmp.get(rowId);
  if (rowId && agentsById.has(rowId)) return agentsById.get(rowId);
  return null;
}

/**
 * Determine Listing End vs Selling End for an agent commission row.
 */
function resolveEndSide(trade, agentRow, agentIndex) {
  const tradeClass = String(trade?.keyInfo?.classification || "").toUpperCase();
  const rowClass = String(agentRow?.classification || "").toUpperCase();
  let transactionType = String(agentRow?.transactionType || "").toUpperCase();

  if (tradeClass === "AGENT DOUBLE ENDER" || rowClass === "AGENT DOUBLE ENDER") {
    if (!transactionType && agentRow?.agentId) {
      const indices = [];
      (trade.agentCommissionList || []).forEach((a, i) => {
        if (
          String(a.agentId) === String(agentRow.agentId) &&
          (String(a.classification || "").toUpperCase() ===
            "AGENT DOUBLE ENDER" ||
            a.transactionType)
        ) {
          indices.push(i);
        }
      });
      const pos = indices.indexOf(agentIndex);
      transactionType = pos === 0 ? "LISTING" : "SELLING";
    }
    return transactionType === "SELLING" ? "selling" : "listing";
  }

  if (tradeClass === "CO-OPERATING SIDE") return "selling";
  return "listing";
}

function parseMoney(value) {
  const n = parseFloat(value);
  return Number.isFinite(n) ? n : 0;
}

/**
 * Build pending trades report grouped by agent, then Listing/Selling end.
 */
export function buildPendingTradeReport(trades, agents, filters) {
  const {
    fromDate,
    toDate,
    agentId,
    status,
    classification,
    propertyType,
    sortBy,
  } = filters;

  const selectedAgent =
    agentId && agents?.length
      ? agents.find((a) => String(a._id) === String(agentId))
      : null;

  const agentsByEmp = new Map();
  const agentsById = new Map();
  for (const a of agents || []) {
    if (a.employeeNo != null) agentsByEmp.set(String(a.employeeNo), a);
    if (a._id != null) agentsById.set(String(a._id), a);
  }

  const filteredTrades = (trades || [])
    .filter((t) => {
      const s = String(t?.keyInfo?.status || "").toUpperCase();
      return s !== "CLOSED" && s !== "EXPIRED" && s !== "FALLEN THRU";
    })
    .filter((t) => matchesStatusFilter(t, status))
    .filter((t) => matchesClassification(t, classification))
    .filter((t) => matchesPropertyType(t, propertyType))
    .filter((t) => pendingTradeDateInRange(t, sortBy, fromDate, toDate))
    .sort((a, b) => {
      const da = getPendingTradeSortDate(a, sortBy)?.getTime() || 0;
      const db = getPendingTradeSortDate(b, sortBy)?.getTime() || 0;
      if (da !== db) return da - db;
      const na = parseInt(String(a.tradeNumber).replace(/\D/g, ""), 10) || 0;
      const nb = parseInt(String(b.tradeNumber).replace(/\D/g, ""), 10) || 0;
      return na - nb;
    });

  /** @type {Map<string, object>} */
  const groups = new Map();

  for (const trade of filteredTrades) {
    const k = trade.keyInfo || {};
    const list = trade.agentCommissionList || [];
    const saleVolume = parseMoney(k.sellPrice);

    list.forEach((row, agentIndex) => {
      if (isReferralRow(row)) return;
      if (!agentRowMatchesSelected(row, selectedAgent)) return;

      const master = resolveAgentMaster(row, agentsByEmp, agentsById);
      const employeeNo =
        master?.employeeNo != null
          ? String(master.employeeNo)
          : String(row.agentId || "").trim();
      const groupKey =
        employeeNo ||
        String(row.agentName || master?._id || "unknown").toLowerCase();
      const groupLabel = formatAgentGroupLabel(
        master || {
          employeeNo,
          agentName: row.agentName,
          firstName: "",
          lastName: "",
        },
      );

      const pct =
        row.percentage != null && row.percentage !== ""
          ? Number(row.percentage)
          : null;
      const ends =
        pct != null && Number.isFinite(pct) ? Math.round((pct / 100) * 100) / 100 : 0;
      const commission = parseMoney(row.amount || row.awardAmount);
      const fees = parseMoney(row.totalFees || row.feesDeducted);
      const payouts = parseMoney(row.buyerRebateAmount);
      const agentNet = parseMoney(row.netCommission) || Math.max(0, commission - fees);
      const endSide = resolveEndSide(trade, row, agentIndex);

      if (!groups.has(groupKey)) {
        groups.set(groupKey, {
          groupKey,
          groupLabel,
          employeeNo,
          sortName: formatAgentSortName(master) || String(row.agentName || ""),
          listingRows: [],
          sellingRows: [],
        });
      }

      const reportRow = {
        tradeNumber: trade.tradeNumber,
        tradeId: trade._id,
        type: propertyTypeAbbrev(k.propertyType || k.type),
        address: formatTradeAddress(trade),
        firmCond: firmCondFlag(trade),
        listingNumber: k.listingNumber || "",
        offerDate: formatReportDate(k.offerDate),
        closeDate: formatReportDate(
          k.closeDate || trade.commission?.saleClosingRows?.[0]?.closingDate,
        ),
        pctOfEnd: pct != null && Number.isFinite(pct) ? pct : null,
        ends,
        saleVolume,
        commission,
        fees,
        payouts,
        agentNet,
        endSide,
      };

      const g = groups.get(groupKey);
      if (endSide === "selling") g.sellingRows.push(reportRow);
      else g.listingRows.push(reportRow);
    });
  }

  const sumSection = (rows) =>
    rows.reduce(
      (acc, r) => {
        acc.ends += r.ends || 0;
        acc.saleVolume += r.saleVolume || 0;
        acc.commission += r.commission || 0;
        acc.fees += r.fees || 0;
        acc.payouts += r.payouts || 0;
        acc.agentNet += r.agentNet || 0;
        return acc;
      },
      {
        ends: 0,
        saleVolume: 0,
        commission: 0,
        fees: 0,
        payouts: 0,
        agentNet: 0,
        count: rows.length,
      },
    );

  const agentGroups = Array.from(groups.values())
    .map((g) => {
      const listingTotals = sumSection(g.listingRows);
      const sellingTotals = sumSection(g.sellingRows);
      return {
        ...g,
        listingTotals: {
          ...listingTotals,
          ends: Math.round(listingTotals.ends * 100) / 100,
          saleVolume: Math.round(listingTotals.saleVolume * 100) / 100,
          commission: Math.round(listingTotals.commission * 100) / 100,
          fees: Math.round(listingTotals.fees * 100) / 100,
          payouts: Math.round(listingTotals.payouts * 100) / 100,
          agentNet: Math.round(listingTotals.agentNet * 100) / 100,
        },
        sellingTotals: {
          ...sellingTotals,
          ends: Math.round(sellingTotals.ends * 100) / 100,
          saleVolume: Math.round(sellingTotals.saleVolume * 100) / 100,
          commission: Math.round(sellingTotals.commission * 100) / 100,
          fees: Math.round(sellingTotals.fees * 100) / 100,
          payouts: Math.round(sellingTotals.payouts * 100) / 100,
          agentNet: Math.round(sellingTotals.agentNet * 100) / 100,
        },
      };
    })
    .filter((g) => g.listingRows.length > 0 || g.sellingRows.length > 0)
    .sort((a, b) => {
      const empA = parseInt(a.employeeNo, 10) || 0;
      const empB = parseInt(b.employeeNo, 10) || 0;
      if (empA !== empB) return empA - empB;
      return a.sortName.localeCompare(b.sortName, undefined, {
        sensitivity: "base",
      });
    });

  const allRows = agentGroups.flatMap((g) => [
    ...g.listingRows,
    ...g.sellingRows,
  ]);
  const summary = sumSection(allRows);
  summary.ends = Math.round(summary.ends * 100) / 100;
  summary.saleVolume = Math.round(summary.saleVolume * 100) / 100;
  summary.commission = Math.round(summary.commission * 100) / 100;
  summary.fees = Math.round(summary.fees * 100) / 100;
  summary.payouts = Math.round(summary.payouts * 100) / 100;
  summary.agentNet = Math.round(summary.agentNet * 100) / 100;
  summary.tradeCount = new Set(allRows.map((r) => String(r.tradeNumber))).size;

  return { agentGroups, summary };
}
